import { useCallback, useRef, useState } from "react";

/**
 * Browser-side media client for a V2 agent session.
 *
 * V2 has no LiveKit - its only media path is the WebSocket at /ws/plivo, which
 * speaks the Plivo stream protocol (base64 16kHz mono PCM in JSON frames). The
 * server does not care who is on the other end, so the browser streams the mic
 * over that same socket instead of a phone leg. Nothing in the V2 backend
 * changes for this to work.
 *
 * The session itself is created by the normal POST /api/call flow - this hook
 * only carries the audio, so the call log, organization lookup and assistant
 * config are identical to V1.
 */

const SAMPLE_RATE = 16000;
// 20ms of 16-bit mono audio. Matches the playback chunk size V2 sends back.
const FRAME_SAMPLES = (SAMPLE_RATE * 20) / 1000;

// At the start of a playback run (fresh call or post-barge-in), warm the output
// pipeline with this much silence before the first real audio, so the first real
// sample isn't the hardware's cold start. Doubles as the one-time lead that keeps
// the first real buffer off the clock edge.
const PLAYBACK_PREROLL_SEC = 0.08;
// Ramp the first real buffer up from zero over this window so its opening sample
// doesn't step off digital silence with a click. ~25ms - inaudible as a fade.
const PLAYBACK_FADE_SEC = 0.025;

export type V2CallEvent = {
  timestamp: string;
  type: string;
  description: string;
  status?: string;
};

type StartOptions = {
  /** Fully-formed media socket URL returned by /api/call (carries callUuid + ctx). */
  wsUrl: string;
  onEvent?: (event: V2CallEvent) => void;
};

/**
 * The worklet runs on the audio thread and just forwards raw Float32 blocks;
 * conversion to PCM16 happens on the main thread where the socket lives.
 */
const CAPTURE_WORKLET = `
class CaptureProcessor extends AudioWorkletProcessor {
  process(inputs) {
    const input = inputs[0];
    if (input && input[0]) this.port.postMessage(input[0].slice(0));
    return true;
  }
}
registerProcessor('v2-capture', CaptureProcessor);
`;

function floatToPcm16(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const clamped = Math.max(-1, Math.min(1, input[i]));
    out[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
  }
  return out;
}

function pcm16ToBase64(pcm: Int16Array): string {
  const bytes = new Uint8Array(pcm.buffer, pcm.byteOffset, pcm.byteLength);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

function base64ToFloat32(payload: string) {
  const binary = atob(payload);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const pcm = new Int16Array(bytes.buffer, bytes.byteOffset, bytes.byteLength / 2);
  const out = new Float32Array(pcm.length);
  for (let i = 0; i < pcm.length; i++) out[i] = pcm[i] / 0x8000;
  return out;
}

export function useV2WebCall() {
  const [connected, setConnected] = useState(false);
  const [micEnabled, setMicEnabled] = useState(true);
  // True while agent audio is arriving; drops shortly after the last chunk. UI-only, so
  // a screen can show who is talking without touching the audio path.
  const [agentSpeaking, setAgentSpeaking] = useState(false);
  const speakingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const captureCtxRef = useRef<AudioContext | null>(null);
  const playbackCtxRef = useRef<AudioContext | null>(null);
  const workletUrlRef = useRef<string | null>(null);
  const pendingRef = useRef<Float32Array[]>([]);
  const pendingLenRef = useRef(0);
  const micEnabledRef = useRef(true);
  const endedRef = useRef(false);

  // Playback scheduling: each 20ms frame is queued at the tail of the previous
  // one (playheadRef) rather than played on arrival.
  const playheadRef = useRef(0);
  const sourcesRef = useRef<AudioBufferSourceNode[]>([]);

  const onEventRef = useRef<((event: V2CallEvent) => void) | undefined>();
  const onClosedRef = useRef<((reason: string) => void) | undefined>();

  const emit = useCallback((type: string, description: string, status?: string) => {
    onEventRef.current?.({
      timestamp: new Date().toISOString(),
      type,
      description,
      status,
    });
  }, []);

  const stopPlayback = useCallback(() => {
    for (const source of sourcesRef.current) {
      try {
        source.stop();
      } catch {
        // already finished
      }
    }
    sourcesRef.current = [];
    // Reset the playhead so the next run re-primes from the clock.
    playheadRef.current = 0;
    if (speakingTimerRef.current) clearTimeout(speakingTimerRef.current);
    setAgentSpeaking(false);
  }, []);

  const disconnect = useCallback(
    (reason: string) => {
      if (endedRef.current) return;
      endedRef.current = true;

      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        try {
          ws.send(JSON.stringify({ event: "stop" }));
        } catch {
          // socket already going away
        }
      }
      try {
        ws?.close();
      } catch {
        // ignore
      }
      wsRef.current = null;

      stopPlayback();
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      void captureCtxRef.current?.close().catch(() => {});
      void playbackCtxRef.current?.close().catch(() => {});
      captureCtxRef.current = null;
      playbackCtxRef.current = null;
      if (workletUrlRef.current) {
        URL.revokeObjectURL(workletUrlRef.current);
        workletUrlRef.current = null;
      }
      pendingRef.current = [];
      pendingLenRef.current = 0;

      setConnected(false);
      emit("Disconnected", `Call ended: ${reason}`, "disconnected");
      onClosedRef.current?.(reason);
    },
    [emit, stopPlayback],
  );

  const scheduleBuffer = useCallback((samples: Float32Array) => {
    const ctx = playbackCtxRef.current;
    if (!ctx || !samples.length) return;

    // playhead === 0 means the first buffer of a run: fresh call or post-barge-in
    // reset. Warm the output with a silent pre-roll and fade the first real
    // buffer in, to kill the cold-start click/noise burst.
    const isRunStart = playheadRef.current === 0;

    if (isRunStart) {
      const prerollLen = Math.floor(SAMPLE_RATE * PLAYBACK_PREROLL_SEC);
      const preroll = ctx.createBuffer(1, prerollLen, SAMPLE_RATE);
      const prerollSrc = ctx.createBufferSource();
      prerollSrc.buffer = preroll;
      prerollSrc.connect(ctx.destination);
      prerollSrc.start(ctx.currentTime);
      playheadRef.current = ctx.currentTime + preroll.duration;
      sourcesRef.current.push(prerollSrc);
      prerollSrc.onended = () => {
        sourcesRef.current = sourcesRef.current.filter((s) => s !== prerollSrc);
      };
    }

    const buffer = ctx.createBuffer(1, samples.length, SAMPLE_RATE);
    buffer.copyToChannel(samples, 0);

    const source = ctx.createBufferSource();
    source.buffer = buffer;

    const startAt = Math.max(ctx.currentTime, playheadRef.current);

    if (isRunStart) {
      const fade = ctx.createGain();
      fade.gain.setValueAtTime(0, startAt);
      fade.gain.linearRampToValueAtTime(1, startAt + PLAYBACK_FADE_SEC);
      source.connect(fade);
      fade.connect(ctx.destination);
    } else {
      source.connect(ctx.destination);
    }

    source.start(startAt);
    playheadRef.current = startAt + buffer.duration;

    sourcesRef.current.push(source);
    source.onended = () => {
      sourcesRef.current = sourcesRef.current.filter((s) => s !== source);
    };
  }, []);

  const playChunk = useCallback(
    (payload?: string) => {
      if (!payload) return;
      const samples = base64ToFloat32(payload);
      if (!samples.length) return;
      scheduleBuffer(samples);
      setAgentSpeaking(true);
      if (speakingTimerRef.current) clearTimeout(speakingTimerRef.current);
      speakingTimerRef.current = setTimeout(() => setAgentSpeaking(false), 400);
    },
    [scheduleBuffer],
  );

  const start = useCallback(
    async ({ wsUrl, onEvent }: StartOptions) => {
      onEventRef.current = onEvent;
      endedRef.current = false;

      emit("Dialing", "Requesting microphone access", "dialing");
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;

      const captureCtx = new AudioContext({ sampleRate: SAMPLE_RATE });
      captureCtxRef.current = captureCtx;
      const playbackCtx = new AudioContext({ sampleRate: SAMPLE_RATE });
      playbackCtxRef.current = playbackCtx;

      // Autoplay policy can leave a fresh context suspended, which silently
      // stops both capture and playback.
      await captureCtx.resume().catch(() => {});
      await playbackCtx.resume().catch(() => {});

      const blobUrl = URL.createObjectURL(
        new Blob([CAPTURE_WORKLET], { type: "application/javascript" }),
      );
      workletUrlRef.current = blobUrl;
      await captureCtx.audioWorklet.addModule(blobUrl);

      emit("Dialing", "Connecting to V2 media stream", "dialing");
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      const source = captureCtx.createMediaStreamSource(stream);
      const node = new AudioWorkletNode(captureCtx, "v2-capture");

      node.port.onmessage = (event) => {
        if (ws.readyState !== WebSocket.OPEN) return;
        if (!micEnabledRef.current) return;

        const block = event.data as Float32Array;
        pendingRef.current.push(block);
        pendingLenRef.current += block.length;

        // Pack the 128-sample worklet blocks into 20ms frames before sending.
        while (pendingLenRef.current >= FRAME_SAMPLES) {
          const frame = new Float32Array(FRAME_SAMPLES);
          let filled = 0;
          while (filled < FRAME_SAMPLES) {
            const head = pendingRef.current[0];
            const take = Math.min(head.length, FRAME_SAMPLES - filled);
            frame.set(head.subarray(0, take), filled);
            filled += take;
            if (take === head.length) pendingRef.current.shift();
            else pendingRef.current[0] = head.subarray(take);
          }
          pendingLenRef.current -= FRAME_SAMPLES;

          ws.send(
            JSON.stringify({
              event: "media",
              media: {
                track: "inbound",
                payload: pcm16ToBase64(floatToPcm16(frame)),
              },
            }),
          );
        }
      };

      // A worklet whose output reaches nothing is not pulled by the graph, so
      // it must terminate at the destination. Silence the branch instead, or the
      // mic is echoed back to the speakers.
      const silencer = captureCtx.createGain();
      silencer.gain.value = 0;
      source.connect(node);
      node.connect(silencer);
      silencer.connect(captureCtx.destination);

      await new Promise<void>((resolve, reject) => {
        ws.onopen = () => {
          // V2 keys the session off the start frame; callUuid also comes in on
          // the URL, this mirrors what Plivo sends.
          const callUuid = new URL(wsUrl).searchParams.get("callUuid");
          ws.send(
            JSON.stringify({
              event: "start",
              streamId: callUuid,
              start: { callUuid, streamId: callUuid },
            }),
          );
          setConnected(true);
          setMicEnabled(true);
          micEnabledRef.current = true;
          emit("Connected", "Connected to V2 agent", "connected");
          resolve();
        };
        ws.onerror = () => reject(new Error("Failed to connect to V2 backend"));
      });

      ws.onmessage = (event) => {
        let message: any;
        try {
          message = JSON.parse(event.data);
        } catch {
          return;
        }
        if (message.event === "playAudio") {
          playChunk(message.media?.payload);
        } else if (message.event === "clearAudio") {
          // Barge-in: drop whatever is still queued.
          stopPlayback();
        }
      };

      ws.onclose = () => disconnect("agent-left");
    },
    [disconnect, emit, playChunk, stopPlayback],
  );

  const toggleMic = useCallback(() => {
    micEnabledRef.current = !micEnabledRef.current;
    setMicEnabled(micEnabledRef.current);
  }, []);

  /** Called after the socket is fully torn down, so the caller can end the call
   * log. Receives the teardown reason ("user" | "agent-left" | "unmount"). */
  const setOnClosed = useCallback((handler: (reason: string) => void) => {
    onClosedRef.current = handler;
  }, []);

  return {
    start,
    disconnect,
    toggleMic,
    setOnClosed,
    connected,
    micEnabled,
    agentSpeaking,
  };
}
